import React, { FC } from 'react';

import {
  TypeOfBill,
  TypeOfUser,
  areValuesDifferent,
  convertToDecimals,
  getTypeOfUser,
  userPreferredTimeFormat,
} from '@pharmaplan/common';

import useBillDetails from '../../../hooks/BillDetails/useBillDetails';
import { getTimeWithDiff } from '../../Modals/WorkshiftDetails/utils';
import { useAppSelector } from '../../../hooks/useAppSelector';
import useSelfService from '../../../hooks/useSelfService';
import useAdmin from '../../../hooks/useAdmin';
import strings from '../../../localization';

import ServiceCard from '../ServiceCard/ServiceCard';
import BillContent from './BillContent/BillContent';
import BillFooter from './BillFooter/BillFooter';
import { IBillDetails, IConfigData } from './types';

const { pharmacist, pharmacy } = TypeOfUser;
export enum BillAdjustmentType {
  bonus,
  discount
}

const BillDetails: FC<IBillDetails> = ({
  billType,
  event,
  isCounterOffer,
  successAction,
  showTitle = true,
  isAutoSyncRecord,
  customTitle,
  EndAdornment,
}) => {
  const { counterStartDate = '', counterEndDate = '' } = event ?? {};
  const { isSelfService } = useSelfService();

  const { bookingDetailsConfig } = useBillDetails({ event, isAutoSyncRecord });

  const userType = useAppSelector(getTypeOfUser);
  const { isAdmin } = useAdmin();

  const isPharmacist = userType === pharmacist;
  const isPharmacy = userType === pharmacy;

  const suffix = isPharmacy ? ` + ${strings.tax}` : '';

  const timeFormat = useAppSelector(userPreferredTimeFormat);

  const counterOfferTime = getTimeWithDiff(
    counterStartDate,
    counterEndDate,
    timeFormat,
  );

  const rateAndTime = isPharmacist
    ? [
      {
        key: '0',
        name: strings.time,
        value: '',
        data: true,
        counterOfferData: counterStartDate,
        counterOfferValue: `${counterOfferTime}`,
      },
    ]
    : [];

  const counterOfferCriteria = isCounterOffer
    ? {
      checkCriteria: 'counterOfferData',
      checker: areValuesDifferent,
    }
    : {
      checkCriteria: 'data',
      checker: () =>
        true,
    };

  const billTotal = convertToDecimals(
    (bookingDetailsConfig)?.reduce(
      (acc, curr: IConfigData) =>
        acc + parseFloat((curr.amount ?? '0') as string),
      0,
    ),
  );

  let counterOfferBillTotal = 0;

  if (isSelfService) {
    counterOfferBillTotal = convertToDecimals(
      (bookingDetailsConfig)?.reduce(
        (acc, curr: IConfigData) =>
          acc + parseFloat((curr.counterOfferAmount ?? '0') as string),
        0,
      ),
    ) as number;
  }

  const cardTitle = () => {
    switch (billType) {
      case TypeOfBill.bill:
        return strings.billDetails;
      case TypeOfBill.finalBill:
        return strings.contractDetails;
      case TypeOfBill.counterOffer:
        return isPharmacist || isAdmin
          ? strings.counterOffer
          : strings.billDetails;
      default:
        return strings.billDetails;
    }
  };

  return (
    <ServiceCard
      title={showTitle ? (customTitle ?? cardTitle()) : null}
      isCounterOffer={isCounterOffer}
      content={(
        <BillContent
          successAction={successAction}
          counterOfferCriteria={counterOfferCriteria}
          rateAndTime={rateAndTime}
          billDetails={bookingDetailsConfig}
          isCounterOffer={isCounterOffer}
        />
      )}
      footer={(
        <>
          <BillFooter
            successAction={successAction}
            suffix={suffix}
            total={billTotal}
            counterTotal={isCounterOffer && counterOfferBillTotal}
          />
          {EndAdornment}
        </>
      )}
    />
  );
};

export default BillDetails;
